import { putUserNameApi, putUserSettingsApi, putUserTipApi } from "../api";
import { DEFAULT_SETTINGS, type SettingKey, type TipKey, type UserSettings, type UserTips } from "../settings";
import { isSignedIn } from "./init";
import { items, type UserProfile } from "./items";

/** The signed-in user's profile, or null when signed out. Cached for an hour. */
export const getProfile = (forceRefresh = false): Promise<UserProfile | null> => items.profile.get(forceRefresh);

export const getSettings = (): Promise<UserSettings> => items.settings.get();

/** A setting's value, falling back to its default. */
export const getSetting = async (key: SettingKey): Promise<string> => {
  const settings = await items.settings.get();
  return settings[key] ?? DEFAULT_SETTINGS[key];
};

export type SettingChange = { key: SettingKey; value: string };

/** Saves to the API when signed in, then always to the cache, so settings also work signed out. */
export const putSettings = async (changes: SettingChange[]): Promise<void> => {
  if (await isSignedIn()) {
    await putUserSettingsApi.invoke({ settings: changes });
  }
  // One write after the other would race on the same item, so merge once.
  const settings = await items.settings.get();
  const merged: Record<string, string> = { ...settings };
  for (const { key, value } of changes) {
    merged[key] = value;
  }
  items.settings.set(merged as UserSettings);
  const profile = items.profile.peek();
  if (profile) {
    items.profile.set({ ...profile, settings: merged as UserSettings });
  }
};

export const putSetting = (key: SettingKey, value: string): Promise<void> => putSettings([{ key, value }]);

export const getTips = (): Promise<UserTips> => items.tips.get();

export const isTipDismissed = async (key: TipKey): Promise<boolean> => (await items.tips.get())[key] === true;

/** A tip that fails to save is still dismissed locally. */
export const putTip = async (tipKey: TipKey, dismissed: boolean): Promise<void> => {
  if (await isSignedIn()) {
    await putUserTipApi.invoke({ tipKey, dismissed }).catch((error: unknown) => {
      console.error("Failed to save user tip", error);
    });
  }
  const tips = await items.tips.get();
  items.tips.set({ ...tips, [tipKey]: dismissed });
};

export const updateName = async (name: string): Promise<string> => {
  const result = await putUserNameApi.invoke({ name });
  const profile = items.profile.peek();
  if (profile) {
    items.profile.set({ ...profile, name: result.name });
  }
  return result.name;
};
